<script setup>
import { computed } from 'vue';

const props = defineProps({
  value: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  label: {
    type: String,
    required: true,
    validator: value => value.trim().length > 0,
  },
  color: {
    type: String,
    default: 'blue',
    validator: value =>
      ['blue', 'brand', 'teal', 'ruby', 'amber', 'violet'].includes(value),
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
});

const safeMax = computed(() =>
  Number.isFinite(props.max) && props.max > 0 ? props.max : 100
);
const safeValue = computed(() => {
  if (!Number.isFinite(props.value)) return 0;
  return Math.min(Math.max(props.value, 0), safeMax.value);
});

const colorClasses = computed(
  () =>
    ({
      blue: '[&::-webkit-progress-value]:bg-n-blue-9 [&::-moz-progress-bar]:bg-n-blue-9',
      brand:
        '[&::-webkit-progress-value]:bg-n-brand [&::-moz-progress-bar]:bg-n-brand',
      teal: '[&::-webkit-progress-value]:bg-n-teal-9 [&::-moz-progress-bar]:bg-n-teal-9',
      ruby: '[&::-webkit-progress-value]:bg-n-ruby-9 [&::-moz-progress-bar]:bg-n-ruby-9',
      amber:
        '[&::-webkit-progress-value]:bg-n-amber-9 [&::-moz-progress-bar]:bg-n-amber-9',
      violet:
        '[&::-webkit-progress-value]:bg-n-violet-9 [&::-moz-progress-bar]:bg-n-violet-9',
    })[props.color]
);
</script>

<template>
  <progress
    :value="safeValue"
    :max="safeMax"
    :aria-label="label"
    :aria-valuenow="safeValue"
    role="progressbar"
    class="block w-full appearance-none overflow-hidden rounded-full border-0 bg-n-alpha-2 [&::-webkit-progress-bar]:bg-n-alpha-2 [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:transition-[width] [&::-webkit-progress-value]:duration-300 motion-reduce:[&::-webkit-progress-value]:transition-none [&::-moz-progress-bar]:rounded-full"
    :class="[colorClasses, size === 'sm' ? 'h-1.5' : 'h-2']"
  />
</template>
